import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    ArrowsRightLeftIcon,
    GlobeAltIcon,
    ShieldCheckIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    TicketIcon,
    ChevronDownIcon,
} from '@heroicons/react/24/outline';

const API_URL = '/api/v1';

export default function TransferWizard() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const certIdFromUrl = searchParams.get('certId');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [certificate, setCertificate] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [availableCerts, setAvailableCerts] = useState([]);
    const [selectedCertId, setSelectedCertId] = useState(certIdFromUrl || '');

    const [formData, setFormData] = useState({
        toHolderId: '',
        amount: '',
        destinationType: 'INTERNAL',
        transferReason: 'TRADE',
        identityHeader: localStorage.getItem('userEmail') || ''
    });

    // Load accounts and available certs on mount
    useEffect(() => {
        const fetchFixed = async () => {
            const email = localStorage.getItem('userEmail') || 'admin@map.go';
            const role = localStorage.getItem('userRole') || 'ADMIN';
            const headers = { 'X-User-Email': email, 'X-User-Role': role };

            try {
                const [accRes, certsRes] = await Promise.all([
                    axios.get(`${API_URL}/accounts`, { headers }),
                    axios.get(`${API_URL}/certificates`, { headers })
                ]);
                setAccounts(accRes.data);
                setAvailableCerts(certsRes.data.filter(c => c.status === 'ISSUED' || c.status === 'ACTIVE'));
            } catch (err) {
                console.error('Fetch error:', err);
            }
        };
        fetchFixed();
    }, []);

    // Load selected cert details when cert changes
    useEffect(() => {
        if (!selectedCertId) {
            setCertificate(null);
            return;
        }
        const fetchCert = async () => {
            const email = localStorage.getItem('userEmail') || 'admin@map.go';
            const role = localStorage.getItem('userRole') || 'ADMIN';
            const headers = { 'X-User-Email': email, 'X-User-Role': role };

            try {
                const res = await axios.get(`${API_URL}/certificates/${selectedCertId}`, { headers });
                setCertificate(res.data);
                setFormData(prev => ({ ...prev, amount: Number(res.data.output_mwh) || 1.000 }));
            } catch (err) {
                console.error('Cert fetch error:', err);
            }
        };
        fetchCert();
    }, [selectedCertId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                certificate_id: selectedCertId,
                from_holder_id: certificate.account_holder_id,
                to_holder_id: formData.toHolderId,
                amount_mwh: parseFloat(formData.amount),
                from_registry: certificate.country_of_origin || 'MAPGO',
                to_registry: formData.destinationType === 'HUB' ? 'EU_HUB' : (certificate.country_of_origin || 'MAPGO'),
                transfer_reason: formData.transferReason,
                identity_signature: formData.identityHeader
            };

            await axios.post(`${API_URL}/transfers`, payload);
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Transfer failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-xl mx-auto py-20 text-center animate-fade-in shadow-xl p-10 bg-white border border-emerald-100">
                <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircleIcon className="w-12 h-12 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4 uppercase tracking-tight">Transfer Finalized</h2>
                <p className="text-sm text-gray-500 mb-8 leading-relaxed font-medium">
                    The electronic certificates have been successfully routed to the destination holder ledger.
                </p>
                <button onClick={() => navigate('/transfers')} className="btn-grexel px-10 py-3 rounded-sm">
                    View History
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto py-8">
            <div className="mb-10 text-center">
                <h1 className="text-2xl font-bold text-gray-800 mb-1 uppercase tracking-tight font-black">Electronic Transfer Wizard</h1>
                <p className="text-sm text-gray-500 font-medium">Route green assets internally</p>
            </div>

            {error && (
                <div className="mb-8 p-4 rounded-sm bg-red-50 border border-red-100 text-red-600 flex items-center gap-3">
                    <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                    <p className="text-xs font-bold uppercase tracking-tight">{error}</p>
                </div>
            )}

            {/* Certificate Selection (shown when not pre-selected) */}
            {!certIdFromUrl && (
                <div className="mb-8 glass-card p-6 rounded-none border-gray-200 bg-white shadow-sm">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
                        Select Certificate to Transfer *
                    </label>
                    <div className="relative">
                        <select
                            className="w-full bg-white border border-gray-300 p-3 pr-10 text-xs font-bold text-gray-800 rounded-sm outline-none focus:border-[#155e75] appearance-none"
                            value={selectedCertId}
                            onChange={(e) => setSelectedCertId(e.target.value)}
                        >
                            <option value="">Choose an available certificate batch...</option>
                            {availableCerts.map(c => (
                                <option key={c.certificate_id} value={c.certificate_id}>
                                    {c.gs1_id} — {parseFloat(c.output_mwh).toLocaleString()} MWh [{c.status}]
                                </option>
                            ))}
                        </select>
                        <ChevronDownIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    {availableCerts.length === 0 && (
                        <p className="text-[10px] text-red-500 mt-2 font-bold uppercase tracking-widest">
                            No issued certificates available in wallet. Request issuance first.
                        </p>
                    )}
                </div>
            )}

            {certificate ? (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-2 space-y-6">
                        <div className="glass-card p-8 rounded-none border-gray-200 bg-white shadow-sm">
                            <div className="flex items-center gap-3 mb-10 border-b border-gray-100 pb-4">
                                <ArrowsRightLeftIcon className="w-6 h-6 text-[#155e75]" />
                                <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest">EECS Transfer Request</h3>
                            </div>

                            <div className="space-y-8">
                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, destinationType: 'INTERNAL' })}
                                        className={`flex-1 p-4 rounded-sm border transition-all flex flex-col items-center ${formData.destinationType === 'INTERNAL' ? 'bg-blue-50 border-[#155e75] text-[#155e75]' : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-gray-300'}`}
                                    >
                                        <ShieldCheckIcon className="w-8 h-8 mb-2" />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Internal Domain</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, destinationType: 'HUB' })}
                                        className={`flex-1 p-4 rounded-sm border transition-all flex flex-col items-center ${formData.destinationType === 'HUB' ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-gray-300'}`}
                                    >
                                        <GlobeAltIcon className="w-8 h-8 mb-2" />
                                        <span className="text-[10px] font-black uppercase tracking-widest">International Hub</span>
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Transferor Origin (Account Holder ID)</label>
                                    <input
                                        type="text"
                                        disabled
                                        value={certificate.account_holder_id}
                                        className="w-full bg-gray-50 border border-gray-200 p-3 text-xs font-mono text-gray-400 rounded-sm"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Recipient Account *</label>
                                    <div className="relative">
                                        <select
                                            required
                                            className="w-full bg-white border border-gray-300 p-3 pr-10 text-xs font-bold text-gray-800 rounded-sm outline-none focus:border-[#155e75] appearance-none"
                                            value={formData.toHolderId}
                                            onChange={(e) => setFormData({ ...formData, toHolderId: e.target.value })}
                                        >
                                            <option value="">Select registry participant...</option>
                                            {accounts.map(acc => (
                                                <option key={acc.id} value={acc.id}>{acc.account_name} ({acc.account_number})</option>
                                            ))}
                                        </select>
                                        <ChevronDownIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Transfer Volume (MWh) *</label>
                                    <div className="relative">
                                        <input
                                            required
                                            type="number"
                                            step="1"
                                            min="1"
                                            max={Math.floor(parseFloat(certificate.output_mwh))}
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            className="w-full bg-white border border-gray-300 p-3 text-sm font-bold text-gray-800 rounded-sm font-mono focus:border-[#155e75] outline-none"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-[10px] uppercase">MWh</span>
                                    </div>
                                    <p className="text-[10px] text-gray-400 ml-1">
                                        Available: <span className="font-black text-[#155e75]">{Math.floor(parseFloat(certificate.output_mwh)).toLocaleString()} MWh</span>
                                        {' '}(whole units only — EECS rule)
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Transfer Reason *</label>
                                        <div className="relative">
                                            <select
                                                className="w-full bg-white border border-gray-300 p-3 pr-10 text-xs font-bold text-gray-800 rounded-sm outline-none focus:border-[#155e75] appearance-none"
                                                value={formData.transferReason}
                                                onChange={(e) => setFormData({ ...formData, transferReason: e.target.value })}
                                            >
                                                <option value="TRADE">Market Trade / OTC</option>
                                                <option value="PPA">PPA Settlement</option>
                                                <option value="CROSS_BORDER">Cross-Border Transfer</option>
                                                <option value="DISCLOSURE">National Disclosure</option>
                                                <option value="PORTFOLIO">Portfolio Rebalance</option>
                                            </select>
                                            <ChevronDownIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Identity Header / Signature *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="User Email or Role ID"
                                            value={formData.identityHeader}
                                            onChange={(v) => setFormData({ ...formData, identityHeader: v.target.value })}
                                            className="w-full bg-white border border-gray-300 p-3 text-xs font-bold text-gray-800 rounded-sm outline-none focus:border-[#155e75]"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="glass-card p-6 rounded-none border-gray-200 bg-gray-50 shadow-inner">
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                <TicketIcon className="w-4 h-4 text-[#155e75]" />
                                Technical Manifest
                            </h4>
                            <div className="space-y-6">
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-1">GIAI Batch</p>
                                    <p className="text-xs font-mono text-gray-700 font-bold border-b border-gray-100 pb-2 break-all">
                                        {certificate.gs1_id}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-1">Certificate Range</p>
                                    <p className="text-xs font-mono text-[#155e75] font-bold border-b border-gray-100 pb-2">
                                        {Math.floor(certificate.start_serial)} — {Math.floor(certificate.end_serial)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-1">Execution Action</p>
                                    <p className="text-2xl font-bold text-gray-800 tracking-tight">{formData.amount || 0} <span className="text-sm text-gray-400 font-medium">MWh</span></p>
                                </div>
                                <div className="pt-6">
                                    <button
                                        type="submit"
                                        disabled={loading || !formData.toHolderId || !formData.amount}
                                        className="w-full bg-[#155e75] hover:bg-[#0e4e61] p-4 rounded-sm font-black text-white text-[11px] uppercase tracking-widest shadow-lg transition-all disabled:opacity-30"
                                    >
                                        {loading ? 'Processing...' : 'Execute Transfer'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            ) : (
                !selectedCertId && (
                    <div className="py-20 text-center text-gray-400 uppercase tracking-widest font-black text-xs italic bg-white border border-dashed border-gray-200">
                        Select a certificate batch above to begin the transfer.
                    </div>
                )
            )}
        </div>
    );
}
